<script>
import { Bar } from 'vue-chartjs'
import {
  addDays,
  format,
  isSameDay,
  addHours,
  differenceInCalendarDays,
  isSameWeek,
  addWeeks,
  addMonths,
  isSameMonth,
  startOfWeek,
} from 'date-fns'

export default {
  extends: Bar,

  props: {
    coupons: { type: Array, default: null },
    period: { type: Object, default: null },
  },

  data: () => ({}),

  computed: {
    binSize() {
      if (this.period.range === 'year') {
        return 'month'
      }

      const nDays = differenceInCalendarDays(
        this.period.end,
        this.period.start
      )

      return nDays > 42 ? 'week' : 'day'
    },
  },

  watch: {
    coupons: {
      handler() {
        this.render()
      },
    },
    period: {
      handler() {
        this.render()
      },
    },
  },

  mounted() {
    this.render()
  },

  methods: {
    render() {
      if (!this.coupons) return

      let dates
      if (this.binSize === 'month') {
        dates = this.getDatesPerMonth(this.coupons)
      } else if (this.binSize === 'week') {
        dates = this.getDatesPerWeek(this.coupons)
      } else {
        dates = this.getDatesPerDay(this.coupons)
      }

      this.renderChart(
        {
          labels: dates.map(d => d.date),
          datasets: [
            {
              label: 'Vouchers created',
              backgroundColor: '#8C6B59',
              data: dates.map(d => d.created),
              borderWidth: 1,
            },
            {
              label: 'Vouchers used',
              backgroundColor: '#704730',
              data: dates.map(d => d.used),
              borderWidth: 1,
            },
          ],
        },
        {
          title: {
            display: true,
            text: 'Campaign statistics',
            fontSize: 20,
          },
          tooltips: {
            mode: 'index',
            intersect: false,
            titleFontSize: 18,
            bodyFontSize: 18,
            callbacks: {
              title: (tooltipItems, data) => {
                const date = data.labels[tooltipItems[0].index]
                if (this.binSize === 'month') {
                  return format(date, 'MMMM')
                }
                if (this.binSize === 'week') {
                  return `Week ${format(date, 'W (D MMMM YYYY)')}`
                }
                return format(date, 'D MMMM YYYY')
              },
            },
          },
          legend: {
            labels: {},
            maintainAspectRatio: false,
          },
          responsive: true,
          scales: {
            xAxes: [
              {
                stacked: false,
                borderDash: 2,
                gridLines: {
                  display: false,
                },
                ticks: {
                  callback: (date, index, values) => {
                    if (this.binSize === 'month') {
                      return format(date, 'MMMM')
                    }
                    if (this.binSize === 'week') {
                      return `Week ${format(date, 'W')}`
                    }
                    return format(date, 'D MMM')
                  },
                },
              },
            ],
            yAxes: [
              {
                stacked: false,
                borderDash: 2,
                gridLines: {
                  display: true,
                },
                ticks: {
                  beginAtZero: true,
                },
              },
            ],
          },
        }
      )
    },

    getDatesPerMonth(coupons) {
      const dates = []
      let currentDate = new Date(this.period.start)
      // To prevent and extra day due to daylight savings
      const endDate = addDays(addHours(this.period.end, -12), 1)
      while (currentDate < endDate) {
        dates.push({ date: currentDate, created: 0, used: 0 })
        currentDate = addMonths(currentDate, 1)
      }

      for (const e of coupons) {
        const date = dates.find(d => isSameMonth(d.date, new Date(e.created_at)))
        if (date) {
          if (e.used_at) {
            date.used++
          }
          date.created++
        } else {
          console.error('Date not found:', e.created_at)
        }
      }

      return dates
    },

    getDatesPerWeek(coupons) {
      const dates = []
      let currentDate = startOfWeek(new Date(this.period.start), {
        weekStartsOn: 1,
      })
      // To prevent and extra day due to daylight savings
      const endDate = addDays(addHours(this.period.end, -12), 1)
      while (currentDate < endDate) {
        dates.push({ date: currentDate, created: 0, used: 0 })
        currentDate = startOfWeek(addWeeks(currentDate, 1), {
          weekStartsOn: 1,
        })
      }

      for (const e of coupons) {
        const date = dates.find(d =>
          isSameWeek(d.date, new Date(e.created_at), { weekStartsOn: 1 })
        )
        if (date) {
          if (e.used_at) {
            date.used++
          }
          date.created++
        } else {
          console.error('Date not found:', e.created_at)
        }
      }

      return dates
    },

    getDatesPerDay(coupons) {
      const dates = []
      let currentDate = new Date(this.period.start)
      // To prevent and extra day due to daylight savings
      const endDate = addDays(addHours(this.period.end, -12), 1)
      while (currentDate < endDate) {
        dates.push({ date: currentDate, created: 0, used: 0 })
        currentDate = addDays(currentDate, 1)
      }

      for (const e of coupons) {
        const date = dates.find(d => isSameDay(d.date, new Date(e.created_at)))
        if (date) {
          if (e.used_at) {
            date.used++
          }
          date.created++
        } else {
          console.error('Date not found:', e.created_at)
        }
      }

      return dates
    },
  },
}
</script>

<style>
</style>
